<template>
    <div class="flex m-0 border-gray-200 draggable hover:z-20">

        <!-- Draggable handle. -->
        <div class="w-6 py-1.5 m-0 -mt-px border-t border-b bg-white">
            <div class="w-5 h-5 flex items-center pl-1">
                <i class="fa fa-bars fa-fw text-xs text-gray-300 hover:text-gray-400 cursor-move handle"
                   v-if="!estimate.readonly"></i>
            </div>
        </div>

        <div class="flex-1 -ml-px mr-px -mt-px">
            <div class="flex ml-px mr-px">
                <!-- Component -->
                <estimate-task-item-component :estimate="estimate" :item="material" :ref="'component'" @componentUpdated="updateComponent"></estimate-task-item-component>
                <!-- Item. -->
                <estimate-task-item-material-search
                    class="flex-1 flex "
                    #default="{ open, query, setQuery }"
                    :store="store"
                    :estimate="estimate"
                    @selected:material="materialSelected"
                    :top="true"
                >
                    <div class="m-0 -ml-px p-0 border border-gray-200 relative">
                        <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'"
                                                ref="autoresizable-textarea" v-if="!estimate.readonly">
                        <textarea
                            placeholder="Item..."
                            class="text-sm m-0 px-2 pt-1.5 pb-0.5 border-1 border-white shadow-none relative hover:z-10 focus:z-20 resize-none"
                            type="text"
                            ref="name"
                            v-model="material.name"
                            rows="1"
                            @input="setQuery($event.target.value);updateName()"
                            @focus="setQuery($event.target.value);open()"
                            @blur="estimate.buildCollectedItems()"
                            @change="estimate.buildCollectedItems()"
                        ></textarea>
                        </autoresizable-textarea>
                        <div v-else
                             class="text-sm m-0 px-2 pt-1.5 pb-0.5 whitespace-pre-wrap border-none shadow-none relative hover:z-10 focus:z-20 resize-none bg-white"
                             v-text="material.name"
                        ></div>
                        <page-cell
                            class="text-xs text-blue-500 border-1 border-white m-0 px-2 py-1 bg-white focus:z-20"
                            placeholder="Supplier..."
                            v-model="material.supplier"
                            :read-only="estimate.readonly"
                            ref="supplier"
                            @input="updateSupplier()"
                            @blur="estimate.buildCollectedItems()"
                            @change="estimate.buildCollectedItems()"
                        ></page-cell>

                        <div class="absolute bottom-1.5 right-1 z-20" v-if="!editAsAssembly">
                            <button class="p-0.5 w-5 h-5 text-xs text-gray-300 hover:text-blue-400"
                                    @click="toggleNotes">
                                <i class="far fa-edit"></i>
                            </button>
                        </div>
                    </div>
                </estimate-task-item-material-search>

                <!-- UoM. -->
                <div class="w-16 -ml-px flex">
                    <page-cell
                        currency
                        class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-white"
                        placeholder="Uom..."
                        :read-only="estimate.readonly"
                        v-model="uom"
                        ref="uom"
                        @input="updateUom"
                        @change="updateAll"
                        @blur="updateAll"
                    ></page-cell>
                </div>

                <!-- Cost. -->
                <div class="w-24 -ml-px flex">
                    <page-cell
                        currency
                        class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-white"
                        placeholder="Cost..."
                        :read-only="estimate.readonly"
                        v-model="cost"
                        ref="cost"
                        @input="updateCost"
                        @change="updateAll"
                        @blur="updateAll"
                    ></page-cell>
                </div>

                <!-- Coverage. -->
                <div class="w-20 -ml-px flex">
                    <page-cell
                        class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-blue-50"
                        placeholder="Coverage..."
                        :read-only="estimate.readonly"
                        v-model="coverage"
                        ref="coverage"
                        @input="updateCoverage"
                        @change="updateAll"
                        @blur="updateAll"
                    ></page-cell>
                </div>

                <!-- Waste. -->
                <div class="w-16 -ml-px flex">
                    <page-cell
                        class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-blue-50"
                        placeholder="Waste..."
                        :read-only="estimate.readonly"
                        v-model="waste"
                        ref="waste"
                        @input="updateWaste"
                        @change="updateAll"
                        @blur="updateAll"
                    ></page-cell>
                </div>

                <!-- Quantity. -->
                <div class="w-20 -ml-px flex" v-if="!editAsAssembly">
                    <page-cell v-if="item.quantity"
                               class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-blue-50"
                               placeholder="Quantity..."
                               :read-only="estimate.readonly"
                               v-model="quantity"
                               ref="quantity"
                               @input="updateQuantity"
                               @change="updateAll"
                               @blur="updateAll"
                    ></page-cell>
                    <page-cell v-else
                               class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-blue-50 text-red-400"
                               read-only
                               value="???"
                    ></page-cell>
                </div>

                <!-- Total. -->
                <div class="w-28 -ml-px flex" v-if="!editAsAssembly">
                    <page-cell v-if="item.quantity"
                               currency
                               class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-white"
                               placeholder="Total..."
                               :read-only="estimate.readonly"
                               v-model="total"
                               ref="total"
                               @input="updateTotal"
                               @blur="updateAll"
                    ></page-cell>
                    <page-cell v-else
                               class="text-sm m-0 px-2 py-1.5 border border-gray-200 text-red-400"
                               read-only
                               value="???"
                    ></page-cell>
                </div>
            </div>
            <div class="m-0 py-px pb-2" v-if="this.shownotes && !editAsAssembly">
                <div class="m-0 px-px py-px pb+px flex">
                    <div class="w-10 text-gray-700 text-xs pt-0.5 pb-0.5 pl-2 font-medium">Notes:</div>
                    <div class="flex-1">
                        <autoresizable-textarea :height="el => (el.scrollHeight + 1) + 'px'"
                                                ref="notes-textarea" v-if="!estimate.readonly">
                            <textarea
                                placeholder="..."
                                class="col-span-full text-gray-700 text-xs pt-0.5 ml-2  pb-0.5 border-none shadow-none relative hover:z-10 focus:z-20 resize-none focus:ring-0"
                                type="text"
                                ref="notes"
                                v-model="material.notes"
                                rows="1"
                                @input="estimate.update()"
                                @blur="estimate.update()"
                            ></textarea>
                        </autoresizable-textarea>
                        <div v-else
                             class="w-full whitespace-pre-wrap text-gray-700 text-xs ml-2 pt-0.5 pb-0.5 border-none shadow-none relative hover:z-10 focus:z-20 resize-none focus:ring-0"
                             v-text="material.notes"
                        ></div>

                    </div>
                </div>
            </div>
        </div>
        <!-- Dropdown. -->
        <div class="w-6 pt-2 m-0 -ml-px -mt-px border-t border-b bg-white">
            <ui-dropdown menu-class="p-3 w-72">
                <template #default="{ toggle }">
                    <button @click="toggle"
                            class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                        <i class="fa fa-ellipsis-h fa-fw pl-1 pr-1"></i>
                    </button>
                </template>
                <template #menu="{ close }">
                    <ui-dropdown-item icon="fas fa-cut" @click="cutMaterial();close();" v-if="!estimate.readonly">Cut
                        Material
                    </ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-copy" @click="copyMaterial();close();">Copy Material
                    </ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-paste" @click="pasteMaterials();close();"
                                      v-if="materialClipboard.length && !estimate.readonly">
                        {{
                            'Paste Material' + (materialClipboard.length > 1 ? 's' : '') + ' (' + materialClipboard.length + ')'
                        }}
                    </ui-dropdown-item>
                    <hr v-if="materialClipboard.length" class="m-1.5"/>
                    <ui-dropdown-item icon="fas fa-clipboard" @click="clearClipboard();close();"
                                      v-if="materialClipboard.length">
                        {{ 'Empty Material clipboard' }}
                    </ui-dropdown-item>
                    <hr v-if="!estimate.readonly" class="m-1.5"/>
                    <ui-dropdown-item icon="fas fa-trash" v-if="!estimate.readonly" danger
                                      @click="deleteMaterial();close();">Delete
                    </ui-dropdown-item>
                </template>
            </ui-dropdown>
        </div>
    </div>
</template>

<script>
import EstimateTaskItemMaterialSearch from "./EstimateTaskItemMaterialSearch.vue";
import AutoresizableTextarea from "../../../ui/AutoresizableTextarea";
import {Estimate} from "@models";
import {cloneDeep, trim} from "lodash";
import UiDropdownItem from "../../../ui/UiDropdownItem.vue";
import {resolveNumericText} from "../../../../utils";
import remoteStorage from "../../../../utils/remoteStorage";
import PageCell from "../../../ui/PageCell.vue";
import EstimateTaskItemComponent from "./EstimateTaskItemComponent.vue";

export default {
    components: {
        EstimateTaskItemComponent,
        PageCell, UiDropdownItem, AutoresizableTextarea, EstimateTaskItemMaterialSearch},
    props: {
        material: Object,
        item: Object,
        editAsAssembly: false,
        estimate: Estimate,
        store: Object,
        materialClipboard: Array,
    },
    data() {
        return {
            shownotes: this.material.notes !== '',
            cost: this.material.cost,
            uom: this.material.uom,
            coverage: Math.round(this.material.coverage * 1000) / 1000,
            waste: this.material.waste + '%',
            quantity: Math.round(this.material.quantity * 1000) / 1000,
            total: this.material.total,
            updating: false,
            resolveNumericText,
            color:'#FFFFFF',
        }
    },
    watch: {
        item: {
            handler(newValue, oldValue) {
                if (this.updating !== 'cost') {
                    this.cost = this.material.cost;
                }
                if (this.updating !== 'quantity') {
                    this.quantity = Math.round(this.material.quantity * 1000) / 1000;
                }
                if (this.updating !== 'coverage') {
                    this.coverage = Math.round(this.material.coverage * 1000) / 1000;
                }
                if (this.updating !== 'total') {
                    this.total = this.material.total;
                }
                if (this.updating !== 'waste') {
                    this.waste = this.material.waste + '%';
                }
                if (this.updating !== 'uom') {
                    this.uom = this.material.uom;
                }
            },
            deep: true,
        },
    },
    methods: {
        toggleNotes() {
            this.shownotes = !this.shownotes
        },
        updateName() {
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update()
        },
        updateSupplier() {
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update()
        },
        updateComponent() {
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update()
        },
        updateUom() {
            this.updating = 'uom'
            this.material.uom = this.uom
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update()
        },
        updateCost() {
            this.updating = 'cost'
            let value = parseFloat(this.cost === '.' ? '0.' : this.resolveNumericText(this.cost))
            this.material.cost = Math.round((isNaN(value) ? 0 : value) * 100) / 100
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update()
            this.total = this.material.total
        },
        updateCoverage() {
            this.updating = 'coverage';
            let value = parseFloat(this.coverage === '.' ? '0.' : this.resolveNumericText(this.coverage));
            this.material.coverage = isNaN(value) ? 0 : value;
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update();
            this.total = this.material.total;
        },
        updateWaste() {
            this.updating = 'waste';
            let value = parseFloat(this.quantity === '.' ? '0.' : this.resolveNumericText(this.waste));
            this.material.waste = Math.round((isNaN(value) ? 0 : value) * 100) / 100
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update();
            this.total = this.material.total;
        },
        updateQuantity() {
            this.updating = 'quantity';
            let value = parseFloat(this.quantity === '.' ? '0.' : this.resolveNumericText(this.quantity));
            this.material.quantity = isNaN(value) ? 0 : value;
            this.material.coverage = this.material.quantity ? this.item.quantity / (this.material.quantity * (1 - (this.material.waste / 100))) : 0
            this.estimate.update();
            this.total = this.material.total;
        },
        updateTotal() {
            this.updating = 'total';
            let value = parseFloat(this.total === '.' ? '0.' : this.resolveNumericText(this.total));
            this.material.total = isNaN(value) ? 0 : value;
            if (this.material.cost) {
                this.material.quantity = this.material.total ? (this.material.total / this.material.cost) : 0
                this.material.coverage = this.material.total ? (this.item.quantity / (this.material.quantity * (1 - (this.material.waste / 100)))) : 0
            }
            this.estimate.update();
            this.material.total = isNaN(value) ? 0 : value;
        },
        updateAll() {
            this.cost = this.material.cost;
            this.quantity = Math.round(this.material.quantity * 1000) / 1000;
            this.coverage = Math.round(this.material.coverage * 1000) / 1000;
            this.total = this.material.total;
            this.waste = this.material.waste + '%';
            this.estimate.buildCollectedItems()
            this.updating = false;
        },
        materialSelected({material, supplierName, purchaseCost}) {
            this.material.uom = trim(material.unitOfMeasure);
            this.material.name = trim(material.name);
            this.material.supplier = trim(supplierName);
            this.cost = parseFloat(purchaseCost.replace(/[^0-9.]/g, ''));
            this.updateCost();
        },
        deleteMaterial() {
            this.$emit('assemblyItemRemoved', this.material, 'material')
        },
        cutMaterial() {
            this.materialClipboard.length = 0;
            this.materialClipboard.push(this.material);
            remoteStorage.setItem('estimate-material-clipboard', JSON.stringify(this.materialClipboard))
            this.$emit('assemblyItemRemoved', this.material, 'material')
        },
        copyMaterial() {
            this.materialClipboard.length = 0;
            this.materialClipboard.push(cloneDeep(this.material))
            remoteStorage.setItem('estimate-material-clipboard', JSON.stringify(this.materialClipboard))
        },
        pasteMaterials() {
            let num = this.item.materials.indexOf(this.material);
            this.materialClipboard.forEach((t, index) => {
                let material = cloneDeep(t);
                this.item.materials.splice(num + index + 1, 0, material);
                this.$emit('assemblyItemAdded', material, 'material')
            });
            this.estimate.update()
        },
        clearClipboard() {
            this.materialClipboard.length = 0;
            remoteStorage.setItem('estimate-material-clipboard', JSON.stringify(this.materialClipboard));
        },
    },
}
</script>
